import { useState } from 'react';
import { ContextProp, GroupedVirtuoso } from 'react-virtuoso';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ListingTileItem } from 'src/tiles/ListingTilesV2';
import { PriceRangeGroupItem } from 'src/tiles/ListingTilesV2/CompListingTile';
import { InventoryListingTileWrapper } from 'src/tiles/ListingTilesV2/ListingTilesV2.components';
import { useGroupedVirtuoso } from 'src/tiles/ListingTilesV2/ListingTilesV2.hooks';
import { isPriceRangeGroup } from 'src/tiles/ListingTilesV2/ListingTilesV2.utils';
import { Event, Listing } from 'src/WebApiController';

import { MobileDrawerVenueMap } from '../components/MobileDrawerVenueMap';
import { MobileListingSectionSummary } from '../components/MobileListingSectionSummary';
import { MobileListingStats } from '../components/MobileListingStats';
import { MobilePriceInput } from '../components/MobilePriceInput';
import { MobileListingActionsDrawer } from '../MobileListingActionsDrawer';
import { useCompListingTileItems } from './MobileListingDetailsView.hooks';

type ListingDetailsVirtuosoContext = {
  listing: Listing;
  event: Event;
};

const Header = ({
  context: { event, listing },
}: ContextProp<ListingDetailsVirtuosoContext>) => {
  const [showActions, setShowActions] = useState(false);
  return (
    <Stack
      height="full"
      width="full"
      direction="column"
      gap="l"
      style={{ paddingBottom: vars.spacing.m }}
    >
      <MobileDrawerVenueMap listing={listing} />
      <Stack
        direction="column"
        gap="l"
        style={{ padding: `0 ${vars.spacing.m}` }}
      >
        <MobileListingStats
          event={event}
          listing={listing}
          onOpenActions={() => setShowActions(true)}
        />
        <MobilePriceInput listing={listing} />
        <MobileListingSectionSummary />
      </Stack>
      <MobileListingActionsDrawer
        listing={listing}
        event={event}
        show={showActions}
        onClose={() => setShowActions(false)}
      />
    </Stack>
  );
};

export const MobileListingDetailsView = ({
  listing,
  event,
}: {
  listing: Listing;
  event: Event;
}) => {
  const compListingTileItems = useCompListingTileItems();

  const { localGroupCounts, onUpdateExpanded, getGroupItemIndexFromItemIndex } =
    useGroupedVirtuoso(compListingTileItems);

  const { getUiCurrency } = useLocalizationContext();
  const currency = getUiCurrency(listing?.currency);

  return (
    <GroupedVirtuoso<ListingTileItem, ListingDetailsVirtuosoContext>
      context={{ listing, event }}
      components={{
        Header: Header,
      }}
      groupCounts={localGroupCounts}
      groupContent={(groupIndex) => {
        const listingItem = compListingTileItems[groupIndex];
        if (!listingItem) {
          return null;
        }
        return (
          <div style={{ padding: `0 ${vars.spacing.m} ${vars.spacing.xxs}` }}>
            <InventoryListingTileWrapper
              listing={listingItem}
              event={event}
              entities={[]}
              expanded={localGroupCounts[groupIndex] > 0}
              onUpdateExpanded={onUpdateExpanded(groupIndex)}
              currency={currency}
            />
          </div>
        );
      }}
      itemContent={(itemIndex, groupIndex) => {
        const group = compListingTileItems[groupIndex];
        if (!isPriceRangeGroup(group)) {
          return null;
        }
        const groupItemIndex = getGroupItemIndexFromItemIndex(
          itemIndex,
          groupIndex
        );
        const item = group.groupItems[groupItemIndex];
        if (item) {
          return (
            <div style={{ padding: `0 ${vars.spacing.m} ${vars.spacing.xxs}` }}>
              <PriceRangeGroupItem item={item} />
            </div>
          );
        }
      }}
    />
  );
};
